<template>
    <div class="feedback-section">
        <div class="feedback-card" :class="feedback.is_correct ? 'correct' : 'incorrect'">
            <div class="feedback-header">
                <h3>{{ feedback.is_correct ? "Correct" : "Incorrect" }}</h3>
                <div class="score">{{ feedback.score }}/{{ feedback.max_score }}</div>
            </div>

            <div class="feedback-text">
                <strong>Feedback</strong>
                <p ref="feedbackTextRef">
                    <vue-writer
                        :typeSpeed="18"
                        :iterations="1"
                        :array="[feedback.feedback]"
                        @typed="handleTypingComplete"
                    ></vue-writer>
                </p>
            </div>

            <div class="worked-solution">
                <strong>Worked Solution</strong>
                <div class="solution-content" v-html="formatText(feedback.worked_solution)"></div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { VueWriter } from 'vue-writer';
import { useMarkdownRenderer } from '@/composables/useMarkdownRenderer';

const props = defineProps({
    feedback: { type: Object, required: true }
});

const feedbackTextRef = ref(null);
const { formatText, renderMath } = useMarkdownRenderer();

// This fires ONLY when vue-writer finishes the string
const handleTypingComplete = () => {
    if (feedbackTextRef.value) {
        renderMath(feedbackTextRef.value);
    }
};

onMounted(() => {
    // Render the static worked-solution immediately
    const staticSolution = document.querySelector('.worked-solution');
    if (staticSolution) renderMath(staticSolution);
});
</script>

<style scoped>
.feedback-section {
    margin-top: 30px;
}

.feedback-card {
    padding: 25px;
    margin-bottom: 20px;
    border: 2px solid;
}

.feedback-card.correct {
    background: rgba(76, 175, 80, 0.1);
    border-color: #4caf50;
}

.feedback-card.incorrect {
    background: rgba(244, 67, 54, 0.1);
    border-color: #f44336;
}

.feedback-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
}

.feedback-header h3 {
    margin: 0;
    font-size: 24px;
    color: var(--text-primary);
}

.score {
    font-size: 20px;
    font-weight: 700;
    color: var(--text-secondary);
}

.feedback-text,
.worked-solution {
    margin-bottom: 20px;
    text-align: left;
}

.feedback-text strong,
.worked-solution strong {
    display: block;
    margin-bottom: 8px;
    color: var(--accent);
}

.feedback-text p,
.worked-solution p,
.worked-solution .solution-content {
    margin: 0;
    line-height: 1.6;
    color: var(--text-primary);
    white-space: pre-wrap;
    opacity: 0.9;
}

.penalty-notice {
    padding: 12px;
    background: rgba(255, 193, 7, 0.1);
    border-left: 4px solid #ffc107;
    margin-bottom: 20px;
    font-size: 14px;
    color: #ffc107;
}

.worked-solution {
    padding: 15px;
    background: rgba(255, 255, 255, 0.05);
}

/* Tables from v-html content */
.worked-solution :deep(table) {
    width: 100%;
    border-collapse: collapse;
    margin: 12px 0;
    font-size: 14px;
}

.worked-solution :deep(th),
.worked-solution :deep(td) {
    border: 1px solid rgba(var(--accent-rgb), 0.3);
    padding: 8px 12px;
    text-align: left;
}

.worked-solution :deep(th) {
    background: rgba(var(--accent-rgb), 0.15);
    color: var(--text-secondary);
    font-weight: 600;
}

.worked-solution :deep(tr:nth-child(even)) {
    background: rgba(var(--accent-rgb), 0.05);
}

/* Lists from v-html content */
.worked-solution :deep(ul),
.worked-solution :deep(ol) {
    margin: 10px 0;
    padding-left: 24px;
    text-align: left;
}

.worked-solution :deep(li) {
    margin: 4px 0;
    line-height: 1.5;
    list-style-position: outside;
    padding-left: 0;
}

/* KaTeX math expressions - prevent line wrapping */
.worked-solution :deep(.katex) {
    white-space: nowrap;
}

.worked-solution :deep(.katex-display) {
    white-space: nowrap;
    overflow-x: auto;
}

/* Markdown support */
.worked-solution :deep(h1),
.worked-solution :deep(h2),
.worked-solution :deep(h3),
.worked-solution :deep(h4),
.worked-solution :deep(h5),
.worked-solution :deep(h6) {
    margin: 14px 0 10px 0;
    color: var(--accent);
    font-weight: 600;
}

.worked-solution :deep(h1) { font-size: 20px; }
.worked-solution :deep(h2) { font-size: 18px; }
.worked-solution :deep(h3) { font-size: 16px; }
.worked-solution :deep(h4) { font-size: 15px; }
.worked-solution :deep(h5) { font-size: 14px; }
.worked-solution :deep(h6) { font-size: 14px; }

.worked-solution :deep(strong) {
    font-weight: 600;
    color: var(--text-primary);
}

.worked-solution :deep(em) {
    font-style: italic;
}

.worked-solution :deep(code) {
    background: rgba(var(--accent-rgb), 0.15);
    padding: 2px 6px;
    border-radius: 3px;
    font-family: 'Courier New', monospace;
    font-size: 0.9em;
}

.worked-solution :deep(pre) {
    background: rgba(var(--accent-rgb), 0.08);
    padding: 14px;
    border-radius: 4px;
    overflow-x: auto;
    margin: 12px 0;
}

.worked-solution :deep(pre code) {
    background: none;
    padding: 0;
}

.worked-solution :deep(a) {
    color: var(--accent);
    text-decoration: underline;
}

.worked-solution :deep(a:hover) {
    opacity: 0.8;
}

.worked-solution :deep(blockquote) {
    border-left: 4px solid var(--accent);
    padding-left: 14px;
    margin: 12px 0;
    color: var(--text-secondary);
    font-style: italic;
}

.worked-solution :deep(hr) {
    border: none;
    border-top: 1px solid rgba(var(--accent-rgb), 0.3);
    margin: 12px 0;
}

/* Markdown in feedback text */
.feedback-text :deep(h1),
.feedback-text :deep(h2),
.feedback-text :deep(h3),
.feedback-text :deep(h4),
.feedback-text :deep(h5),
.feedback-text :deep(h6) {
    margin: 12px 0 8px 0;
    color: var(--accent);
    font-weight: 600;
}

.feedback-text :deep(h1) { font-size: 18px; }
.feedback-text :deep(h2) { font-size: 16px; }
.feedback-text :deep(h3) { font-size: 15px; }
.feedback-text :deep(h4) { font-size: 14px; }

.feedback-text :deep(strong) {
    font-weight: 600;
}

.feedback-text :deep(em) {
    font-style: italic;
}

.feedback-text :deep(code) {
    background: rgba(var(--accent-rgb), 0.15);
    padding: 2px 6px;
    border-radius: 3px;
    font-family: 'Courier New', monospace;
    font-size: 0.9em;
}

.feedback-text :deep(a) {
    color: var(--accent);
    text-decoration: underline;
}

.feedback-text :deep(blockquote) {
    border-left: 3px solid var(--accent);
    padding-left: 12px;
    margin: 10px 0;
    font-style: italic;
}

/* KaTeX in feedback text */
.feedback-text :deep(.katex) {
    white-space: nowrap;
}

.feedback-text :deep(.katex-display) {
    white-space: nowrap;
    overflow-x: auto;
}
</style>